import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';

import { ArticleSummary, getArticle } from 'stores/ArticleStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

import { KnzComponentProps } from '../../types';
import { ArticleNavContextValue, ArticleNavSlot, useArticleNavContext } from './ArticleNavContext';
import { formatAuthors } from './articleNavUtils';

/** 記事詳細ルート */
const articlePath = (articleId: string) => `/devdocs/article/${articleId}`;

/**
 * 省略（三点リーダー）表示になっている（＝実際にテキストがはみ出している）ときだけ、
 * ホバーで全文をツールチップ表示する Typography。
 * 1 行省略（textOverflow: ellipsis）は scrollWidth、複数行省略（-webkit-line-clamp）は
 * scrollHeight で判定する。はみ出していないときはツールチップを出さない。
 */
const EllipsisTypography: React.FC<
  TypographyProps & { text: string; tooltip?: React.ReactNode }
> = ({ text, tooltip, sx, ...rest }) => {
  const ref = React.useRef<HTMLElement | null>(null);
  const [overflowed, setOverflowed] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 1 行省略: 横方向のはみ出し / 複数行省略: 縦方向のはみ出し
    setOverflowed(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
  }, []);

  React.useEffect(() => {
    checkOverflow();
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow, text]);

  // 常に Tooltip でラップし、はみ出していないときはホバー/フォーカス表示を無効化する。
  // （条件付きでラップを切り替えると測定用 ref が Tooltip の ref 注入で不安定になるため）
  return (
    <Tooltip
      title={tooltip ?? text}
      enterDelay={300}
      disableHoverListener={!overflowed}
      disableFocusListener={!overflowed}
      disableTouchListener={!overflowed}
    >
      <Typography ref={ref} sx={sx} {...rest}>
        {text}
      </Typography>
    </Tooltip>
  );
};

/** 設定エラーを枠内に表示する（プレビューでの入力ミスを致命的にしない）。 */
const NavError: React.FC<{ message: string }> = ({ message }) => (
  <Box sx={{ border: 1, borderColor: 'error.main', borderRadius: 2, p: 1.5, maxWidth: 720 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 18 }} />
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Stack>
  </Box>
);

/** 関連記事の最大表示件数（仕様 Q-7） */
export const MAX_RELATED = 6;

/** 取得状態（articleId → 取得結果 or 失敗） */
type FetchState = {
  loading: boolean;
  /** articleId → 記事（取得できたもののみ）。取得失敗した id は含まれない */
  byId: Map<string, ArticleSummary>;
};

/**
 * `articles`（string[]）から self を起点に前・次・関連の articleId を導出する。
 * - prev: self の 1 つ前 / next: self の 1 つ後
 * - related: self・prev・next を除いた残り
 */
const deriveNav = (
  self: string,
  articles: string[],
): { prevId?: string; nextId?: string; relatedIds: string[] } => {
  const idx = articles.indexOf(self);
  // self が articles に無い場合はパース時に Error になる想定（ここには来ない）が、
  // 念のため防御的に前後なし・全件を関連にする。
  if (idx === -1) {
    return { relatedIds: articles.filter((a) => a !== self) };
  }
  const prevId = idx > 0 ? articles[idx - 1] : undefined;
  const nextId = idx < articles.length - 1 ? articles[idx + 1] : undefined;
  const exclude = new Set([self, prevId, nextId].filter((v): v is string => !!v));
  const relatedIds = articles.filter((a) => !exclude.has(a));
  return { prevId, nextId, relatedIds };
};

/**
 * ArticleNav が実際に「表示する」articleId と、関連指定から「溢れた」articleId を導出する。
 * 記事詳細（ArticleDetail）が、
 * - displayedIds: おすすめ記事から除外する（ArticleNav に既出のため重複させない）
 * - overflowIds: 関連記事の 7 件目以降（表示されない分）。おすすめ記事の先頭に持っていく
 * のに使う。overflowIds は記法（articles）の並び順を保持する。
 */
export const deriveArticleNavIds = (
  self: string,
  articles: string[],
): { displayedIds: string[]; overflowIds: string[] } => {
  const { prevId, nextId, relatedIds } = deriveNav(self, articles);
  const shownRelated = relatedIds.slice(0, MAX_RELATED);
  const overflowIds = relatedIds.slice(MAX_RELATED);
  const displayedIds = Array.from(
    new Set([self, prevId, nextId, ...shownRelated].filter((v): v is string => !!v)),
  );
  return { displayedIds, overflowIds };
};

/** リンク遷移のハンドラを作る（navContext により新規タブ or ルーティング）。 */
export const useOpenArticle = (navContext: ArticleNavContextValue) => {
  const navigate = useNavigate();
  return React.useCallback(
    (articleId: string) => {
      if (navContext === 'preview') {
        window.open(articlePath(articleId), '_blank', 'noopener,noreferrer');
      } else {
        navigate(articlePath(articleId));
      }
    },
    [navContext, navigate],
  );
};

/** 前 / 次の 1 枠。記事が無い（端）or 取得失敗なら無効表示（グレーアウト・遷移不可）。 */
const PrevNextItem: React.FC<{
  direction: 'prev' | 'next';
  article?: ArticleSummary;
  onOpen: (articleId: string) => void;
}> = ({ direction, article, onOpen }) => {
  const isPrev = direction === 'prev';
  const label = isPrev ? '前の記事' : '次の記事';
  const disabled = !article;

  const inner = (
    <Stack
      direction={isPrev ? 'row' : 'row-reverse'}
      spacing={1}
      alignItems="center"
      sx={{ width: '100%' }}
    >
      {isPrev ? (
        <ArrowBackIosNewIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
      ) : (
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
      )}
      <Box sx={{ minWidth: 0, textAlign: isPrev ? 'left' : 'right', flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
        {article ? (
          <EllipsisTypography
            text={article.title}
            variant="body2"
            fontWeight={600}
            sx={{
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          />
        ) : (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            —
          </Typography>
        )}
      </Box>
    </Stack>
  );

  if (disabled) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: 1.5,
          opacity: 0.5,
        }}
      >
        {inner}
      </Box>
    );
  }

  return (
    <ButtonBase
      onClick={() => onOpen(article.articleId)}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'block',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {inner}
    </ButtonBase>
  );
};

/** 前の記事 / 次の記事（上部・下部共通の 2 分割ナビ）。 */
const PrevNextNav: React.FC<{
  prev?: ArticleSummary;
  next?: ArticleSummary;
  onOpen: (articleId: string) => void;
}> = ({ prev, next, onOpen }) => {
  // 前後とも無い場合は何も出さない
  if (!prev && !next) return null;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
      <PrevNextItem direction="prev" article={prev} onOpen={onOpen} />
      <PrevNextItem direction="next" article={next} onOpen={onOpen} />
    </Stack>
  );
};

/**
 * 関連記事の 1 カード。
 * ArticleNav の関連記事のほか、記事詳細の「おすすめ記事」（RecommendedArticles）でも
 * 見た目を揃えるために再利用する。
 */
export const RelatedCard: React.FC<{
  article: ArticleSummary;
  onOpen: (articleId: string) => void;
}> = ({ article, onOpen }) => {
  const authorText = formatAuthors(article.authors);
  return (
    <ButtonBase
      onClick={() => onOpen(article.articleId)}
      sx={{
        display: 'block',
        textAlign: 'left',
        // グリッドのセル幅に追従する（1 行 3 列で左端を揃えるため固定幅にしない）。
        width: '100%',
        minWidth: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        p: 1.5,
        transition: 'background-color 0.2s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
          {article.title}
        </Typography>
        {article.description && (
          <EllipsisTypography
            text={article.description}
            variant="caption"
            color="text.secondary"
            sx={{
              lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          />
        )}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.disabled' }}>
          {authorText && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
              <PersonOutlineIcon sx={{ fontSize: 13 }} />
              <EllipsisTypography
                text={authorText}
                variant="caption"
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              />
            </Stack>
          )}
          <Typography variant="caption">{formatDate(article.createdAt)}</Typography>
        </Stack>
      </Stack>
    </ButtonBase>
  );
};

/**
 * 関連する記事（下部のみ）。
 * 記事詳細の「おすすめ記事」（RecommendedArticles）でも見た目を揃えるために再利用する。
 */
export const RelatedArticles: React.FC<{
  title: string;
  articles: ArticleSummary[];
  onOpen: (articleId: string) => void;
}> = ({ title, articles, onOpen }) => {
  if (articles.length === 0) return null;
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {/*
        1 行 3 列のグリッド（行ごとに左端が揃う）。カードはセル幅に追従する。
        画面が狭いときは 2 列 / 1 列に落として詰まらないようにする。
      */}
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {articles.map((a) => (
          <RelatedCard key={a.articleId} article={a} onOpen={onOpen} />
        ))}
      </Box>
    </Stack>
  );
};

/**
 * 記事ナビ（前後・関連記事）。
 *
 * knz 本文中の 1 箇所に書かれ、KnzViewer が上部（slot='top'）と下部（slot='bottom'）に
 * 振り分けて 2 回描画する。ArticleNavContext のプロバイダ外（＝上下振り分け文脈にいない）では
 * 何も描画しない。
 *
 * - top: 前後ナビのみ
 * - bottom: 前後ナビ ＋ 関連記事
 *
 * 前後・関連の記事メタは getArticle で取得する（self 自身は取得しない＝位置決め専用・仕様 Q-4）。
 * 取得失敗した記事は、関連＝非表示 / 前後＝無効表示（仕様 Q-3）。
 */
export const ArticleNav: React.FC<KnzComponentProps> = ({ node }) => {
  const ctx = useArticleNavContext();

  const self = (node.params.self as string) ?? '';
  const articlesParam = node.params.articles;
  const articles = React.useMemo(
    () => (Array.isArray(articlesParam) ? articlesParam : articlesParam ? [articlesParam] : []),
    [articlesParam],
  );
  const relatedTitle = (node.params.relatedTitle as string) || '関連する記事';

  const { prevId, nextId, relatedIds } = React.useMemo(
    () => deriveNav(self, articles),
    [self, articles],
  );

  // slot に応じて取得対象を決める。top は前後のみ、bottom は前後＋関連。
  const slot: ArticleNavSlot | undefined = ctx?.slot;
  const neededIds = React.useMemo(() => {
    const ids: string[] = [];
    if (prevId) ids.push(prevId);
    if (nextId) ids.push(nextId);
    if (slot === 'bottom') ids.push(...relatedIds.slice(0, MAX_RELATED));
    // 重複除去
    return Array.from(new Set(ids));
  }, [prevId, nextId, relatedIds, slot]);

  const [state, setState] = React.useState<FetchState>({ loading: true, byId: new Map() });

  React.useEffect(() => {
    if (!ctx || neededIds.length === 0) {
      setState({ loading: false, byId: new Map() });
      return;
    }
    let active = true;
    setState({ loading: true, byId: new Map() });
    Promise.allSettled(neededIds.map((id) => getArticle(id))).then((results) => {
      if (!active) return;
      const byId = new Map<string, ArticleSummary>();
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') byId.set(neededIds[i], r.value);
      });
      setState({ loading: false, byId });
    });
    return () => {
      active = false;
    };
    // neededIds は id 配列。join して依存にする（配列参照の揺れを避ける）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, neededIds.join(',')]);

  const onOpen = useOpenArticle(ctx?.navContext ?? 'preview');

  // 下部スロットの実高さを測って親へ通知する（記事詳細の末尾スペーサー調整用）。
  // 上部スロット・プロバイダ外では意味を持たないため 0 を通知する。
  const isBottom = ctx?.slot === 'bottom';
  const reportBottomHeight = ctx?.reportBottomHeight;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!isBottom || !reportBottomHeight) return;
    const el = containerRef.current;
    if (!el) {
      reportBottomHeight(0);
      return;
    }
    // 初期高さを通知し、以後はサイズ変化（取得完了・折り返し）に追従する。
    reportBottomHeight(el.getBoundingClientRect().height);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) reportBottomHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      // アンマウント時は 0 に戻す（別記事へ遷移した際に余白が残らないように）
      reportBottomHeight(0);
    };
    // state の変化（loading→ready）で高さが変わるため state も依存に含める
  }, [isBottom, reportBottomHeight, state]);

  // プロバイダ外（上下振り分けされていない）なら描画しない
  if (!ctx) return null;

  // 設定エラー（仕様 Q-2）。エラーは上部スロットにだけ 1 回出す（上下で二重表示しない）。
  const configError =
    self === ''
      ? 'ArticleNav: self（自身の articleId）が指定されていません'
      : articles.length === 0
        ? 'ArticleNav: articles（記事の articleId 一覧）が指定されていません'
        : !articles.includes(self)
          ? 'ArticleNav: self が articles に含まれていません'
          : null;
  if (configError) {
    return ctx.slot === 'top' ? <NavError message={configError} /> : null;
  }

  if (state.loading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ color: 'text.secondary', py: 1 }}
      >
        <CircularProgress size={16} />
        <Typography variant="caption">記事を読み込み中...</Typography>
      </Stack>
    );
  }

  const prev = prevId ? state.byId.get(prevId) : undefined;
  const next = nextId ? state.byId.get(nextId) : undefined;
  const related =
    ctx.slot === 'bottom'
      ? relatedIds
          .slice(0, MAX_RELATED)
          .map((id) => state.byId.get(id))
          .filter((a): a is ArticleSummary => !!a)
      : [];

  return (
    <Stack ref={isBottom ? containerRef : undefined} spacing={2}>
      <PrevNextNav prev={prev} next={next} onOpen={onOpen} />
      {ctx.slot === 'bottom' && (
        <RelatedArticles title={relatedTitle} articles={related} onOpen={onOpen} />
      )}
    </Stack>
  );
};

export default ArticleNav;
